import { useEffect, useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, Button, Collapse, ListItem } from '@mui/material';
import ChevronDownIcon from '../icons/ChevronDown';
import ChevronRightIcon from '../icons/ChevronRight';
import IconsCreate from 'icons/IconsCreate';

const NavItem = ({
  active = false,
  children,
  depth,
  icon,
  info,
  open: openProp = false,
  path,
  title,
  settings,
  ...other
}) => {
  const [open, setOpen] = useState(openProp);
  const isDiscret = settings?.navbarDiscret;
  const [icons, setIcons] = useState();
  useEffect(() => {
    setIcons(<IconsCreate icon={icon} />);
  }, [icon]);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  let paddingLeft = 16;

  if (depth > 0) {
    paddingLeft = 32 + 8 * depth;
  }

  // Branch
  if (children) {
    return (
      <ListItem
        disableGutters
        sx={{
          display: "flex",
          py: "4px",
          px: "12px",
        }}
        {...other}
      >
        <Button
          endIcon={
            !open ? (
              <ChevronRightIcon fontSize="small" />
            ) : (
              <ChevronDownIcon fontSize="small" />
            )
          }
          onClick={handleToggle}
          startIcon={icon}
          sx={{
            color: isDiscret ? "text.secondary" : "rgba(255, 255, 255, 0.7)",
            fontWeight: 500,
            justifyContent: "flex-start",
            pl: `${paddingLeft}px`,
            pr: "16px",
            py: "12px",
            textAlign: "left",
            textTransform: "none",
            width: "100%",
            borderRadius: "100px",
            "& svg": {
              color: isDiscret ? "text.secondary" : "rgba(255, 255, 255, 0.5)",
            },
            "&:hover": {
              backgroundColor: isDiscret
                ? "action.hover"
                : "rgba(255, 255, 255, 0.05)",
            },
          }}
          variant="text"
        >
          <Box sx={{ flexGrow: 1 }}>{title}</Box>
          {info}
        </Button>
        <Collapse in={open} sx={{ width: "100%" }}>
          {children}
        </Collapse>
      </ListItem>
    );
  }

  // Leaf
  return (
    <ListItem
      disableGutters
      sx={{
        display: "flex",
        py: "4px",
        px: "12px",
      }}
    >
      <Button
        component={path && RouterLink}
        startIcon={icons}
        sx={{
          color: isDiscret ? "text.secondary" : "rgba(255, 255, 255, 0.7)",
          fontWeight: 500,
          justifyContent: "flex-start",
          textAlign: "left",
          pl: `${paddingLeft}px`,
          pr: "16px",
          py: "12px",
          borderRadius: "100px",
          textTransform: "none",
          width: "100%",
          transition: "all 0.2s ease-in-out",
          "& svg": {
            color: isDiscret ? "text.secondary" : "rgba(255, 255, 255, 0.5)",
          },
          "&:hover": {
            backgroundColor: isDiscret
              ? "action.hover"
              : "rgba(255, 255, 255, 0.05)",
          },
          ...(active && {
            color: isDiscret ? "primary.main" : "primary.main",
            backgroundColor: isDiscret ? "primary.alpha12" : "#FFFFFF",
            fontWeight: 700,
            "& svg": {
              color: "primary.main",
            },
            "&:hover": {
              backgroundColor: isDiscret ? "primary.alpha20" : "#FFFFFF",
            },
          }),
        }}
        variant="text"
        to={path}
      >
        <Box sx={{ flexGrow: 1 }}>{title}</Box>
        {info}
      </Button>
    </ListItem>
  );
};

NavItem.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.node,
  depth: PropTypes.number.isRequired,
  icon: PropTypes.node,
  info: PropTypes.node,
  open: PropTypes.bool,
  path: PropTypes.string,
  title: PropTypes.string.isRequired
};

export default NavItem;
